import { Injectable } from "@angular/core";
import { distinctUntilChanged, filter, finalize, forkJoin } from "rxjs";
import { CartProduct } from "../Models/cart-product";
import { AccountService } from "./account.service";
import { CartProductService } from "./cart-product.service";
import { CartService } from "./cart.service";
import { CartOffLineService } from "./cartOffline.service";
import { StorageService } from "./storage.service";

@Injectable({ providedIn: "root" })
export class CartInitializerService {
  constructor(
    private storage: StorageService,
    private accountService: AccountService,
    private cartService: CartService,
    private offlineCartService: CartOffLineService,
    private cartProductService: CartProductService,
  ) {}

  // Called by APP_INITIALIZER before the app starts
  init(): void {
    this.accountService.accountSubject
      .pipe(
        distinctUntilChanged((a, b) => a?.id === b?.id),
        filter((client) => !!client?.id),
        //nothing to synchronize if there are no offline products (the client cart is loaded by the CartService)
        filter(() => this.offlineCartService.cartProducts.length > 0),
      )
      .subscribe(() => {
        //if a user is looged in, we synchronize his cart products (merge offline and online products)
        this.syncCartAfterLogin();
      });
  }

  /**
   * @description read offline products from localstorage, add them to the db then return the full online products list and update cartService
   */
  private syncCartAfterLogin(): void {
    this.cartService.setLoading(true);
    const offline = this.offlineCartService.cartProducts;
    forkJoin(
      offline.map((po) =>
        this.cartService.add(po.configuration!, po.quantity!),
      ),
    ).subscribe({
      next: () => {
        this.storage.removeItem("carts");
        this.offlineCartService.clearCart();
        this.loadOnlineCart();
      },
      error: (err) => {
        console.error("Sync error:", err);
        this.loadOnlineCart();
      },
    });
  }

  /**
   * @description reload the online products from the backend after the synchronization
   */
  private loadOnlineCart(): void {
    this.cartProductService
      .getCurrentClient()
      .pipe(finalize(() => this.cartService.setLoading(false)))
      .subscribe({
        next: (cart: CartProduct[]) => this.cartService._cartSubject.next(cart),
        error: (err) => console.error("Cart load error:", err),
      });
  }
}
